import { chakra } from "@chakra-ui/react";
import { pressableButtonRecipe } from "../../theme/recipes";

const PressableButtonElement = chakra("button", pressableButtonRecipe);

const activeSelector = "&:is(:active,[data-active],[aria-pressed=true])";
const mode = (light, dark) => ({ _light: light, _dark: dark });

const semanticPalettes = {
  red: {
    palette: "red",
    solid: { bg: "brand.red", hover: "brand.redHover", active: "brand.redActive", shadow: "brand.redActive" },
    outline: { text: "brand.redFg", border: "brand.red", shadow: "brand.redBorder", activeText: "brand.redFg" },
    surface: { bg: "brand.red", hover: "brand.redHover", active: "brand.redActive" },
    ghost: { text: "brand.redFg", border: "brand.redBorder", shadow: "brand.redBorder", activeText: "brand.redFg" },
  },
  success: { palette: "green" },
  info: { palette: "blue" },
  warning: {
    palette: "orange",
    solid: { text: "gray.900", activeText: "white" },
  },
  danger: {
    palette: "red",
    solid: { bg: "red.700", hover: "red.600", active: "red.800", shadow: "red.800" },
    outline: { text: "red.700", border: "red.700", activeText: "red.800" },
    surface: { bg: "red.700", hover: "red.600", active: "red.800" },
    ghost: { text: "red.700", border: "red.700", activeText: "red.800" },
  },
  important: { palette: "purple" },
  dark: {
    palette: "gray",
    solid: {
      bg: "gray.900",
      hover: "gray.800",
      active: "gray.800",
      shadow: "gray.700",
      text: "white",
    },
    outline: {
      bg: mode("white", "gray.950"),
      hover: mode("gray.50", "gray.900"),
      active: "gray.200",
      shadow: "gray.300",
      border: "gray.900",
      text: mode("gray.900", "gray.200"),
      activeText: mode("gray.900", "white"),
    },
    surface: {
      bg: "gray.900",
      hover: "gray.800",
      active: "gray.800",
      text: "white",
    },
    ghost: {
      bg: "white",
      text: "gray.900",
      border: "gray.900",
      hover: "gray.50",
      active: "gray.200",
    },
  },
  white: {
    palette: "gray",
    solid: {
      bg: "white",
      hover: "gray.50",
      active: "gray.200",
      shadow: "gray.300",
      text: "gray.900",
    },
    outline: {
      bg: "white",
      hover: "gray.50",
      active: "gray.100",
      shadow: "gray.200",
      border: "gray.300",
      text: "gray.800",
    },
    surface: {
      bg: "white",
      hover: "gray.50",
      active: "gray.100",
      shadow: "gray.200",
      border: "gray.200",
      text: "gray.800",
    },
    ghost: {
      text: "white",
      hover: "whiteAlpha.200",
      active: "whiteAlpha.300",
    },
  },
  transparent: {
    palette: "gray",
    solid: {
      bg: "transparent",
      hover: "whiteAlpha.100",
      active: "whiteAlpha.200",
      shadow: "whiteAlpha.400",
      text: "white",
    },
    outline: {
      bg: "transparent",
      hover: "whiteAlpha.100",
      active: "whiteAlpha.200",
      shadow: "whiteAlpha.500",
      border: "whiteAlpha.600",
      text: "white",
      activeText: "white",
    },
    surface: {
      bg: "transparent",
      hover: "whiteAlpha.100",
      active: "whiteAlpha.200",
      text: "white",
      activeText: "white",
    },
    ghost: {
      bg: "transparent",
      hover: "whiteAlpha.100",
      active: "whiteAlpha.200",
      border: "whiteAlpha.600",
      text: "white",
      activeText: "white",
    },
  },
};

const aliases = {
  primary: "solid",
  secondary: "outline",
  subtle: "surface",
};

const colorOverrides = {
  yellow: {
    solid: { text: "gray.900", activeText: "gray.950" },
    surface: { text: "gray.900", activeText: "gray.950" },
  },
  orange: {
    solid: { text: "gray.900", activeText: "white" },
    surface: { text: "gray.900", activeText: "white" },
  },
};

const getColorConfig = (color, colorPalette) => {
  const colorName = color || colorPalette || "red";
  const semanticConfig = semanticPalettes[colorName];

  if (semanticConfig && !semanticConfig.palette) {
    return semanticConfig;
  }

  const palette = semanticConfig?.palette || colorName;

  return {
    palette,
    ...(colorOverrides[palette] || {}),
    ...(semanticConfig || {}),
  };
};

const cssColor = (color) => {
  if (color && typeof color === "object") {
    return Object.fromEntries(Object.entries(color).map(([key, value]) => [key, cssColor(value)]));
  }

  if (typeof color !== "string") {
    return color;
  }

  if (!color || color.startsWith("#") || color.startsWith("rgb") || color.startsWith("var(")) {
    return color;
  }

  return color.includes(".") ? `var(--chakra-colors-${color.replace(".", "-")})` : color;
};

const getPaletteVariant = (config, variant) => {
  const palette = config.palette;
  const solid = {
    bg: `${palette}.600`,
    hover: `${palette}.500`,
    active: `${palette}.700`,
    shadow: `${palette}.700`,
    text: "white",
  };

  const outline = {
    bg: mode("white", "transparent"),
    hover: mode(`${palette}.50`, "whiteAlpha.100"),
    active: mode(`${palette}.100`, "whiteAlpha.200"),
    shadow: mode(`${palette}.500`, `${palette}.300`),
    border: mode(`${palette}.500`, `${palette}.300`),
    text: mode(`${palette}.500`, `${palette}.300`),
    activeText: mode(`${palette}.700`, `${palette}.200`),
  };

  const surface = {
    bg: solid.bg,
    hover: solid.hover,
    active: solid.active,
    text: solid.text,
    activeText: solid.activeText,
  };

  const ghost = {
    bg: outline.bg,
    hover: outline.hover,
    active: outline.active,
    border: outline.border,
    text: outline.text,
    activeText: outline.activeText,
  };

  return {
    solid: { ...solid, ...config.solid },
    outline: { ...outline, ...config.outline },
    surface: { ...surface, ...config.surface },
    ghost: { ...ghost, ...config.ghost },
  }[variant];
};

const getDisabledStyles = (variant, colors, palette) => {
  const filledDisabled = {
    bg: mode(`${palette}.100`, `${palette}.900`),
    color: mode(`${palette}.500`, `${palette}.300`),
    borderColor: mode(`${palette}.200`, `${palette}.700`),
  };
  const outlinedDisabled = {
    bg: mode(`${palette}.50`, "transparent"),
    color: mode(`${palette}.400`, `${palette}.500`),
    borderColor: mode(`${palette}.300`, `${palette}.500`),
  };
  const disabledColors = variant === "solid" || variant === "surface" ? filledDisabled : outlinedDisabled;
  const disabledShadow = variant === "solid"
    ? mode(`${palette}.300`, `${palette}.700`)
    : variant === "outline"
      ? disabledColors.borderColor
      : "none";

  return {
    bg: disabledColors.bg,
    color: disabledColors.color,
    borderColor: colors.border ? disabledColors.borderColor : "transparent",
    "--button-shadow-color": cssColor(disabledShadow),
    boxShadow: variant === "solid" || variant === "outline"
      ? "0 6px 0 var(--button-shadow-color)"
      : "none",
    opacity: 0.72,
    cursor: "not-allowed",
    transform: "none",
    _hover: {
      bg: disabledColors.bg,
      cursor: "not-allowed",
    },
    _active: {
      bg: disabledColors.bg,
      color: disabledColors.color,
      borderColor: colors.border ? disabledColors.borderColor : "transparent",
      boxShadow: variant === "solid" || variant === "outline"
        ? "0 6px 0 var(--button-shadow-color)"
        : "none",
      transform: "none",
    },
  };
};

const pressableStyles = (colors, depth = "6px") => ({
  "--button-shadow-color": cssColor(colors.shadow),
  boxShadow: `0 ${depth} 0 var(--button-shadow-color)`,
  [activeSelector]: {
    bg: colors.active,
    color: colors.activeText || colors.text,
    borderColor: colors.active,
    transform: `translateY(${depth})`,
    boxShadow: "0 0 0 var(--button-shadow-color)",
  },
  _hover: {
    bg: colors.hover,
    [activeSelector]: {
      bg: colors.active,
      color: colors.activeText || colors.text,
      borderColor: colors.active,
      transform: `translateY(${depth})`,
      boxShadow: "0 0 0 var(--button-shadow-color)",
    },
  },
  _active: {
    bg: colors.active,
    color: colors.activeText || colors.text,
    borderColor: colors.active,
    transform: `translateY(${depth})`,
    boxShadow: "0 0 0 var(--button-shadow-color)",
  },
});

const buildVariantStyles = (variant, colors) => {
  if (variant === "ghost") {
    return {
      bg: colors.bg,
      color: colors.text,
      boxShadow: "none",
      borderWidth: "2px",
      borderColor: colors.border,
      _hover: { bg: colors.hover },
      _active: {
        bg: colors.active,
        color: colors.activeText || colors.text,
        borderColor: colors.border,
      },
    };
  }

  if (variant === "outline") {
    return {
      bg: colors.bg,
      color: colors.text,
      borderWidth: "2px",
      borderColor: colors.border,
      ...pressableStyles(colors),
    };
  }

  if (variant === "surface") {
    return {
      bg: colors.bg,
      color: colors.text,
      borderWidth: "0",
      boxShadow: "none",
      _hover: { bg: colors.hover },
      _active: {
        bg: colors.active,
        color: colors.activeText || colors.text,
        boxShadow: "inset 0 0 0 999px rgba(0, 0, 0, 0.06)",
      },
    };
  }

  return {
    bg: colors.bg,
    color: colors.text,
    borderWidth: "0",
    ...pressableStyles(colors),
  };
};

export const Button = ({
  variant = "solid",
  color,
  colorPalette,
  disabled = false,
  isDisabled = false,
  size = "md",
  radius,
  fontSize,
  children,
  ...props
}) => {
  const isButtonDisabled = disabled || isDisabled || variant === "disabled";
  const normalizedVariant = aliases[variant] || variant;
  const supportedVariant = ["solid", "outline", "surface", "ghost"].includes(normalizedVariant)
    ? normalizedVariant
    : "solid";
  const config = getColorConfig(color, colorPalette);
  const colors = getPaletteVariant(config, supportedVariant);
  const variantStyles = buildVariantStyles(supportedVariant, colors);
  const styles = isButtonDisabled
    ? { ...variantStyles, ...getDisabledStyles(supportedVariant, colors, config.palette) }
    : variantStyles;

  return (
    <PressableButtonElement
      variant={supportedVariant}
      size={size}
      colorPalette={config.palette}
      disabled={isButtonDisabled}
      aria-disabled={isButtonDisabled ? "true" : undefined}
      {...styles}
      {...(radius != null && { borderRadius: radius })}
      {...(fontSize != null && { fontSize })}
      {...props}
    >
      {children}
    </PressableButtonElement>
  );
};

Button.displayName = "Button";
